import { CalendarOutlined, DollarCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Flex, Form, Modal, Select, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { Space } from "antd/lib";
import { AxiosError } from "axios";
import { upgradeCustomerPackage } from "hooks/mutations/customerpackage";
import { invalidateCustomerList } from "hooks/mutations/customerList";
import { getAllUpgradablePackageByCustomerPackageId } from "hooks/queries/package";
import { getAllPaymentMethods } from "hooks/queries/payment-methods";
import ReferralCompletesNote from "components/ReferralCompletesNote.component";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useState } from "react";
import { ErrorResponse, useNavigate } from "react-router-dom";
import { translate } from "shared/languages";
import { CustomerPackageDto } from "shared/types";
import { TaxMode } from "shared/types/enum";
import { formatCurrency, getApiErrorMessage } from "shared/utils";
import { applyPurchaseTax } from "shared/utils/tax";
import { textPx } from "shared/constants/zoom";
import { InclusiveTaxNote } from "components/InclusiveTaxNote.component";

const { Text } = Typography;

const UpgradeCustomerPackageModal = ({
  previousCustomerPackage,
  customerId,
  disabled = false,
}: {
  previousCustomerPackage: CustomerPackageDto;
  customerId: string;
  disabled?: boolean;
}) => {
  const {
    id: customerPackageId,
    remaining_credits: oldRemainingSession,
    onhold_credits: oldOnholdSession,
    total_credits: oldTotalSessions,
  } = previousCustomerPackage;
  // The old package's CATALOG price, matching exactly what the server
  // subtracts when it computes upgrade_fee (customerpackage.ts). Deliberately
  // NOT the row's purchase_price: that figure may carry a discount or
  // additive tax of its own, and subtracting a taxed amount from an untaxed
  // catalog price (then taxing the difference) both compounds tax and makes
  // the modal disagree with the fee the server actually records. It stays
  // the catalogue price even when the old package was bought at a discount
  // (Andrew, 2026-09-25).
  const previousCustomerPackagePrice = previousCustomerPackage.package_deal.price;
  const [form] = useForm();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Skipped when the button is disabled: there are no targets to offer, and
  // for an already-upgraded package the endpoint answers 404 (it requires the
  // source to be is_valid), which used to show up as a console error on every
  // visit to one.
  const { data: packages, isLoading: packageLoading } =
    getAllUpgradablePackageByCustomerPackageId(customerPackageId, !disabled);
  // Nothing to upgrade to (no bigger live tier left, e.g. the product was
  // deleted): the button stays disabled rather than opening an empty picker.
  const noTargets = !disabled && !packageLoading && !packages?.length;
  const { data: settings } = useBusinessSettings();
  const { data: paymentMethods = [] } = getAllPaymentMethods();
  // Only ADDITIVE tax changes what's collected, so only it gets a breakdown —
  // same rule as the Buy Package modal and the Invoice/Receipt PDF. Under
  // inclusive tax (or tax off) the delta already IS the total.
  const isAdditiveTax =
    !!settings?.tax_enabled &&
    settings.tax_mode === TaxMode.Additive &&
    !!settings.tax_rate;
  const queryClient = useQueryClient();
  const { mutate: upgrade, isPending } = useMutation({
    mutationFn: ({ packageId, paymentMethodId }: { packageId: string; paymentMethodId?: string | null }) =>
      upgradeCustomerPackage(customerPackageId, customerId, packageId, paymentMethodId),
    onSuccess: ({ data: response }) => {
      setOpen(false);
      form.resetFields();
      // This mutation invalidated nothing, alone among the customer package
      // mutations. Measured honestly, it fires no refetch TODAY: this modal is
      // only ever mounted on the package detail page, so the client's list has
      // no active observer at this moment, and invalidating simply marks it
      // stale. The app then reads correctly anyway because the QueryClient is
      // constructed with no options (src/index.js), so staleTime is 0 and the
      // list refetches on its next mount regardless.
      //
      // It is here because that is an accident, not a design: setting a global
      // staleTime is an ordinary optimisation, and the day someone does, every
      // other mutation in this flow keeps working and this one silently starts
      // showing an upgraded-away package as still active. Targeted rather than
      // a bare ["customer_package"] prefix, which would also dump every
      // booking-picker cache.
      queryClient.invalidateQueries({
        queryKey: ["customer_package", "customer", customerId],
      });
      queryClient.invalidateQueries({
        queryKey: ["customer_package", customerPackageId],
      });
      queryClient.invalidateQueries({ queryKey: ["user", customerId] });
      invalidateCustomerList(queryClient);
      navigate(`/customer/package/${response.data.id}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      setOpen(false);
      getApiErrorMessage(error);
    },
  });

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setOpen(true)}
        disabled={disabled || packageLoading || noTargets}
      >
        Upgrade
      </Button>
      <Modal
        title={"Upgrade"}
        open={open}
        okText={translate("button.done")}
        cancelText={translate("button.cancel")}
        okButtonProps={{
          loading: isPending,
          htmlType: "submit" as any,
        }}
        onOk={() => form.submit()}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
      >
        <Form
          form={form}
          layout="vertical"
          name="upgradeCustomerPackage"
          onFinish={(values: { packageId: string; payment_method?: string }) => {
            upgrade({ packageId: values.packageId, paymentMethodId: values.payment_method });
          }}
        >
          <Form.Item
            name="packageId"
            label={translate("label.package_name")}
            rules={[{ required: true, message: `${translate("form.error.select")} ${translate("package")}` }]}
          >
            <Select
              placeholder={`${translate("form.placeholders.select")} ${translate("label.package_name")}`}
              loading={packageLoading}
            >
              {packages?.map((pkg) => (
                <Select.Option key={pkg.id} value={pkg.id}>
                  {pkg.name} - {formatCurrency(pkg.price)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {/* How the client paid the upgrade fee. Optional and placed as in
              BuyPackageModal (after the package, before the breakdown). */}
          <Form.Item name="payment_method" label={translate("paymentMethods.name")}>
            <Select
              allowClear
              placeholder={translate("paymentMethods.select_placeholder")}
              options={paymentMethods.map((method) => ({ label: method.name, value: method.id }))}
            />
          </Form.Item>
          <Form.Item noStyle dependencies={["packageId"]}>
            {({ getFieldValue }) => {
              const selectedPackageId = getFieldValue("packageId");
              const selectedPackage = packages?.find((pk) => pk.id === selectedPackageId);
              if (!selectedPackage) return;

              const newPrice = selectedPackage?.price || 0;
              const oldFinishedSessions = oldTotalSessions - oldRemainingSession;
              const newRemainingSessions = selectedPackage.total_credits - oldFinishedSessions;
              // The pre-tax upgrade delta, then tax on top — the exact same
              // arithmetic the server runs to store upgrade_fee, so what the
              // admin is told to collect is what gets recorded. Floored at 0
              // like the server: a bigger tier priced below the current one
              // costs nothing extra, never a refund.
              const rawToPay = Math.max(0, newPrice - previousCustomerPackagePrice);
              const toPay = settings ? applyPurchaseTax(rawToPay, settings) : rawToPay;
              return (
                <>
                  <Form.Item
                    label={
                      <strong>
                        <DollarCircleOutlined style={{ marginRight: 5 }} />
                        {translate("label.price_breakdown")}
                      </strong>
                    }
                    style={{ marginLeft: 15 }}
                  >
                    <Space direction="vertical">
                      <Text>
                        {translate("label.new_price")}: {formatCurrency(newPrice)}
                      </Text>
                      <Text>
                        {translate("label.old_price")}: {formatCurrency(previousCustomerPackagePrice)}
                      </Text>
                      {/* With additive tax the delta is only the tax BASE, so
                          it steps back to a quiet grey component row and the
                          green emphasis moves to Total Bayar — the amount the
                          admin actually collects. Without tax the delta IS
                          that amount, so it keeps the emphasis itself. */}
                      <Text
                        type={isAdditiveTax ? "secondary" : "success"}
                        strong={!isAdditiveTax}
                        style={isAdditiveTax ? { fontSize: textPx(13) } : undefined}
                      >
                        {translate(isAdditiveTax ? "pdf.subtotal" : "label.to_pay")}:{" "}
                        {formatCurrency(rawToPay)}
                      </Text>
                      <InclusiveTaxNote
                        amount={rawToPay}
                        enabled={settings?.tax_enabled}
                        mode={settings?.tax_mode}
                        rate={settings?.tax_rate}
                        label={settings?.tax_label}
                      />
                      {isAdditiveTax && (
                        <>
                          <Text type="secondary" style={{ fontSize: textPx(13) }}>
                            {settings?.tax_label} ({settings?.tax_rate}%): {formatCurrency(toPay - rawToPay)}
                          </Text>
                          <Text type="success" strong>
                            {translate("analyticsPage.class.total_label")}: {formatCurrency(toPay)}
                          </Text>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                  {!selectedPackage.unlimited && (
                    <Form.Item
                      label={
                        <strong>
                          <CalendarOutlined style={{ marginRight: 5 }} />
                          {translate("label.session_breakdown")}
                        </strong>
                      }
                      style={{ marginLeft: 15 }}
                    >
                      <Space direction="vertical">
                        <Text>
                          {translate("label.finished_session")}: {oldFinishedSessions}
                        </Text>
                        <Text type="success" strong>
                          {translate("label.new_remaining_session")}: {selectedPackage.total_credits} - {oldFinishedSessions} ={" "}
                          {newRemainingSessions}
                        </Text>
                        <Text type="success" strong>
                          {/* Not "Kredit Ditahan BARU", unlike the derived line above:
                              the upgrade carries this count across untouched
                              (`onhold_credits: previous_customer_package.onhold_credits`
                              in upgradeCustomerPackageById) and zeroes the old
                              row. "Baru" implied a recalculation that never
                              happens. The note below already explains that the
                              bookings holding these credits move with it. */}
                          {translate("label.new_onhold_session")}: {oldOnholdSession}
                        </Text>
                        <Text>‼️ {translate("sublabel.new_onhold_session")}</Text>
                      </Space>
                    </Form.Item>
                  )}
                  {/* Says when this upgrade completes a pending referral (it
                      converts it, server-side) and what becomes of the
                      client's intro discount. */}
                  <ReferralCompletesNote
                    customerId={customerId}
                    open={open}
                    basePrice={newPrice}
                    kind="upgrade"
                    style={{ marginBottom: 16 }}
                  />
                  <Form.Item
                    name="agree"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) =>
                          value ? Promise.resolve() : Promise.reject(new Error(translate("form.error.terms_condition"))),
                      },
                    ]}
                    required
                  >
                    {/* The one irreversible confirmation in this modal, so its
                        box gets a heavier border than the AntD default (1px,
                        faint enough to overlook). */}
                    <Flex align={"flex-start"} gap={10} className="upgrade-agree-checkbox">
                      <Checkbox />
                      <strong>{translate("label.agree_to_upgrade")}</strong>
                    </Flex>
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UpgradeCustomerPackageModal;
